/**
 * No real product-photo asset exists anywhere in this project (checked directly — every image
 * file in the repo is a UI reference screenshot, never product photography), and there is no
 * asset pipeline to host one (R2 was dropped earlier for requiring a payment method). Sourcing
 * "actual Stanley Black & Decker part images" from the web isn't a safe substitute either: real
 * SBD product photography is copyrighted brand material, and even a generic stock photo of "a
 * motor" would misrepresent itself as documentation of a specific catalog part it was never a
 * photo of — the same "looks real but isn't" failure this build avoids everywhere else.
 *
 * So these are deliberately original, hand-drawn illustrations (inline SVG, the same technique
 * already used for Dashboard's RegionMap/IllustrativeTrendLine) — richer and more dimensional
 * than a flat single-glyph icon, but never claiming to be a photograph of anything. Each part's
 * real `category` field picks both the illustration and its accent color, reusing the exact
 * palette already used for Dashboard's role-metric cards, not a new one.
 */

type Category =
  | "Motors"
  | "Chucks"
  | "Batteries"
  | "Chargers"
  | "Gearing"
  | "Switches"
  | "Accessories"
  | "Cooling"
  | "Guards"
  | "Bearings"
  | "Drive"
  | "Electronics"
  | "Consumables"
  | "Housings";

const TILE_CLASS: Record<Category, string> = {
  Motors: "bg-status-blue-bg",
  Switches: "bg-status-blue-bg",
  Cooling: "bg-status-blue-bg",
  Batteries: "bg-status-green-bg",
  Consumables: "bg-status-green-bg",
  Chargers: "bg-status-orange-bg",
  Drive: "bg-status-orange-bg",
  Guards: "bg-status-red-bg",
  Gearing: "bg-status-indigo-bg",
  Electronics: "bg-status-indigo-bg",
  Chucks: "bg-status-purple-bg",
  Bearings: "bg-status-purple-bg",
  Accessories: "bg-surface",
  Housings: "bg-surface",
};

function MotorArt() {
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="motorBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6d99f2" />
          <stop offset="100%" stopColor="#2f6fed" />
        </linearGradient>
      </defs>
      <line x1="12" y1="10" x2="12" y2="6" stroke="#2f6fed" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="24" y1="10" x2="24" y2="6" stroke="#2f6fed" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="6" y="10" width="22" height="18" rx="3" fill="url(#motorBody)" />
      <rect x="6" y="10" width="22" height="5.5" rx="3" fill="#ffffff" opacity="0.3" />
      <rect x="28" y="16" width="6" height="6" rx="1.5" fill="#1c4fc4" />
      <circle cx="17" cy="19" r="5" fill="#0a1330" opacity="0.3" />
      <circle cx="17" cy="19" r="2.2" fill="#ffffff" opacity="0.55" />
    </svg>
  );
}

function BatteryArt() {
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="battBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3fcf83" />
          <stop offset="100%" stopColor="#12924f" />
        </linearGradient>
      </defs>
      <rect x="15" y="6" width="10" height="5" rx="1.5" fill="#12924f" />
      <rect x="8" y="10" width="24" height="22" rx="3.5" fill="url(#battBody)" />
      <rect x="8" y="10" width="24" height="6" rx="3.5" fill="#ffffff" opacity="0.28" />
      <rect x="12" y="19" width="16" height="3" rx="1.5" fill="#ffffff" opacity="0.85" />
      <rect x="12" y="24" width="10" height="3" rx="1.5" fill="#ffffff" opacity="0.55" />
    </svg>
  );
}

function ChargerArt() {
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="chgBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f5a95a" />
          <stop offset="100%" stopColor="#b45309" />
        </linearGradient>
      </defs>
      <rect x="7" y="16" width="26" height="14" rx="3" fill="url(#chgBody)" />
      <rect x="7" y="16" width="26" height="4.5" rx="3" fill="#ffffff" opacity="0.3" />
      <rect x="14" y="9" width="4" height="8" rx="1.5" fill="#b45309" />
      <rect x="22" y="9" width="4" height="8" rx="1.5" fill="#b45309" />
      <circle cx="27" cy="24" r="2.4" fill="#ffffff" opacity="0.9" />
      <circle cx="27" cy="24" r="1.1" fill="#b45309" />
    </svg>
  );
}

function GearingArt() {
  const teeth = Array.from({ length: 8 }, (_, i) => (i * 360) / 8);
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="gearBody" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7c86f2" />
          <stop offset="100%" stopColor="#4f46e5" />
        </linearGradient>
      </defs>
      <g transform="translate(20,20)">
        {teeth.map((deg) => (
          <rect key={deg} x="-3" y="-16.5" width="6" height="6" rx="1" fill="#4f46e5" transform={`rotate(${deg})`} />
        ))}
        <circle r="12" fill="url(#gearBody)" />
        <circle r="4.5" fill="#0a1330" opacity="0.35" />
        <circle r="1.8" fill="#ffffff" opacity="0.7" />
      </g>
    </svg>
  );
}

function SwitchArt() {
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="swBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6d99f2" />
          <stop offset="100%" stopColor="#2f6fed" />
        </linearGradient>
      </defs>
      <rect x="7" y="14" width="26" height="12" rx="6" fill="url(#swBody)" />
      <rect x="7" y="14" width="26" height="4" rx="6" fill="#ffffff" opacity="0.28" />
      <circle cx="26" cy="20" r="5" fill="#ffffff" />
      <circle cx="26" cy="20" r="2" fill="#1c4fc4" />
    </svg>
  );
}

function ChuckArt() {
  const jaws = [0, 120, 240];
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="chkBody" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#b48ef0" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
      </defs>
      <circle cx="20" cy="20" r="13" fill="url(#chkBody)" />
      <g transform="translate(20,20)">
        {jaws.map((deg) => (
          <polygon key={deg} points="-3,-12 3,-12 0,-4" fill="#ffffff" opacity="0.85" transform={`rotate(${deg})`} />
        ))}
      </g>
      <circle cx="20" cy="20" r="4" fill="#0a1330" opacity="0.35" />
    </svg>
  );
}

function GuardArt() {
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="grdBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f0645f" />
          <stop offset="100%" stopColor="#d92d20" />
        </linearGradient>
      </defs>
      <path d="M10 30 A14 14 0 0 1 30 30" fill="none" stroke="url(#grdBody)" strokeWidth="6" strokeLinecap="round" />
      <path d="M10 30 A14 14 0 0 1 20 16" fill="none" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function BearingArt() {
  const balls = Array.from({ length: 6 }, (_, i) => (i * 360) / 6);
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="brgBody" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#b48ef0" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
      </defs>
      <circle cx="20" cy="20" r="13" fill="none" stroke="url(#brgBody)" strokeWidth="4.5" />
      <circle cx="20" cy="20" r="5" fill="#0a1330" opacity="0.25" />
      <g transform="translate(20,20)">
        {balls.map((deg) => (
          <circle key={deg} r="2" cy="-13" fill="#ffffff" transform={`rotate(${deg})`} />
        ))}
      </g>
    </svg>
  );
}

function DriveArt() {
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="drvBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f5a95a" />
          <stop offset="100%" stopColor="#b45309" />
        </linearGradient>
      </defs>
      <polygon points="20,7 31,13.5 31,26.5 20,33 9,26.5 9,13.5" fill="url(#drvBody)" />
      <polygon points="20,7 31,13.5 20,20 9,13.5" fill="#ffffff" opacity="0.25" />
      <rect x="16.5" y="16.5" width="7" height="7" fill="#0a1330" opacity="0.35" />
    </svg>
  );
}

function ElectronicsArt() {
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="elcBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7c86f2" />
          <stop offset="100%" stopColor="#4f46e5" />
        </linearGradient>
      </defs>
      <rect x="7" y="7" width="26" height="26" rx="3" fill="url(#elcBody)" />
      <path d="M12 14 H20 V20 H28" fill="none" stroke="#ffffff" strokeOpacity="0.7" strokeWidth="1.6" />
      <path d="M12 26 H17 V20" fill="none" stroke="#ffffff" strokeOpacity="0.7" strokeWidth="1.6" />
      <circle cx="12" cy="14" r="1.6" fill="#ffffff" />
      <circle cx="28" cy="20" r="1.6" fill="#ffffff" />
      <circle cx="12" cy="26" r="1.6" fill="#ffffff" />
      <rect x="21" y="23" width="6" height="6" rx="1" fill="#0a1330" opacity="0.35" />
    </svg>
  );
}

function CoolingArt() {
  const blades = [0, 90, 180, 270];
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="coolBody" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#6d99f2" />
          <stop offset="100%" stopColor="#2f6fed" />
        </linearGradient>
      </defs>
      <g transform="translate(20,20)">
        {blades.map((deg) => (
          <path key={deg} d="M0,0 C 3,-4 3,-11 0,-13 C -3,-11 -3,-4 0,0 Z" fill="url(#coolBody)" transform={`rotate(${deg})`} />
        ))}
        <circle r="4" fill="#0a1330" opacity="0.35" />
      </g>
    </svg>
  );
}

function AccessoryArt() {
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="accBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#a8b3c4" />
          <stop offset="100%" stopColor="#64748b" />
        </linearGradient>
      </defs>
      <polygon points="20,8 27,12 27,20 20,24 13,20 13,12" fill="url(#accBody)" />
      <circle cx="20" cy="16" r="4.5" fill="#0a1330" opacity="0.3" />
    </svg>
  );
}

function ConsumableArt() {
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="conBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3fcf83" />
          <stop offset="100%" stopColor="#12924f" />
        </linearGradient>
      </defs>
      <rect x="17" y="6" width="6" height="6" rx="1.5" fill="#12924f" />
      <path d="M13 12 H27 L25 32 A5 5 0 0 1 15 32 Z" fill="url(#conBody)" />
      <path d="M14.5 18 H25.5" stroke="#ffffff" strokeOpacity="0.5" strokeWidth="1.5" />
    </svg>
  );
}

function HousingArt() {
  return (
    <svg viewBox="0 0 40 40" className="w-full h-full">
      <defs>
        <linearGradient id="houBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#a8b3c4" />
          <stop offset="100%" stopColor="#64748b" />
        </linearGradient>
      </defs>
      <rect x="7" y="10" width="26" height="20" rx="5" fill="url(#houBody)" />
      <line x1="20" y1="10" x2="20" y2="30" stroke="#0a1330" strokeOpacity="0.25" strokeWidth="1.4" />
      <rect x="7" y="10" width="26" height="6" rx="5" fill="#ffffff" opacity="0.25" />
    </svg>
  );
}

const CATEGORY_ART: Record<Category, () => JSX.Element> = {
  Motors: MotorArt,
  Batteries: BatteryArt,
  Chargers: ChargerArt,
  Gearing: GearingArt,
  Switches: SwitchArt,
  Chucks: ChuckArt,
  Guards: GuardArt,
  Bearings: BearingArt,
  Drive: DriveArt,
  Electronics: ElectronicsArt,
  Cooling: CoolingArt,
  Accessories: AccessoryArt,
  Consumables: ConsumableArt,
  Housings: HousingArt,
};

function isKnownCategory(value: string | undefined): value is Category {
  return !!value && value in CATEGORY_ART;
}

export function PartIcon({ category, size = "sm" }: { category: string | undefined; size?: "sm" | "md" }) {
  const known = isKnownCategory(category);
  const Art = known ? CATEGORY_ART[category] : AccessoryArt;
  const tileClass = known ? TILE_CLASS[category] : "bg-surface";
  const dims = size === "sm" ? "w-9 h-9" : "w-11 h-11";
  return (
    <div className={`${dims} rounded-[10px] flex items-center justify-center flex-shrink-0 overflow-hidden ${tileClass}`} title={category}>
      <Art />
    </div>
  );
}
